import { initDataHeader } from "@/telegram/tg";

// Адрес API задаётся на сборке через VITE_API_URL (build-arg в Dockerfile / .env.local),
// свой для КАЖДОГО окружения (прод/тест/локаль). Без него — относительный путь (same-origin):
// заметно ломается в неверном окружении, а не тихо уходит в прод.
export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export class ApiError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail || `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

async function throwApiError(r: Response): Promise<never> {
  let detail = "";
  try {
    const body = (await r.json()) as { detail?: unknown };
    if (typeof body?.detail === "string") detail = body.detail;
  } catch {
    /* non-JSON error body */
  }
  throw new ApiError(r.status, detail);
}

/** Человекопонятный текст ошибки для пользователя. Никогда не показываем сырой
 *  detail/статус — только локализованные сообщения с общим фолбэком. */
export function friendlyError(e: unknown, fallback = "Что-то пошло не так. Попробуйте ещё раз позже."): string {
  if (!(e instanceof ApiError)) return fallback;
  const d = (e.detail || "").toLowerCase();
  switch (e.status) {
    case 400:
      if (d.includes("format") || d.includes("phone")) return "Неверный формат номера. Укажите в формате +7XXXXXXXXXX.";
      if (d.includes("amount")) return "Недопустимая сумма заявки.";
      if (d.includes("address")) return "Неверный адрес кошелька TRC20.";
      return "Проверьте введённые данные.";
    case 401:
      return "Сессия не активна. Войдите снова.";
    case 403:
      return "Действие сейчас недоступно.";
    case 404:
      return "Заявка на звонок не найдена или устарела. Запросите звонок заново.";
    case 409:
      if (d.includes("telegram")) return "Этот Telegram-аккаунт уже привязан к другому номеру телефона.";
      if (d.includes("phone")) return "Этот номер уже используется другим аккаунтом. Войдите с него или напишите в поддержку.";
      return "Действие уже выполнено или конфликтует с текущим состоянием.";
    case 413:
      return "Файл слишком большой. Загрузите фото поменьше.";
    case 429:
      return "Слишком часто. Подождите немного и попробуйте снова.";
    case 502:
    case 503:
      return "Сервис временно недоступен. Попробуйте чуть позже.";
    default:
      return fallback;
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const r = await fetch(API_URL + path, {
    headers: { ...initDataHeader() },
    credentials: "include",
  });
  if (!r.ok) await throwApiError(r);
  return (await r.json()) as T;
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const r = await fetch(API_URL + path, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...initDataHeader() },
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });
  if (!r.ok) await throwApiError(r);
  return (await r.json()) as T;
}

export async function apiPut<T>(path: string, body?: unknown): Promise<T> {
  const r = await fetch(API_URL + path, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...initDataHeader() },
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });
  if (!r.ok) await throwApiError(r);
  return (await r.json()) as T;
}

export async function apiDelete<T>(path: string): Promise<T> {
  const r = await fetch(API_URL + path, {
    method: "DELETE",
    headers: { ...initDataHeader() },
    credentials: "include",
  });
  if (!r.ok) await throwApiError(r);
  return (await r.json()) as T;
}

export type KycAccessToken = { token: string; user_id: string; level: string };
export type IdenfySession = { auth_token: string | null; scan_ref: string | null; redirect_url: string };
export type FmsUnit = { code: string; name: string };
export type IssueDetailsIn = { issued_at: string; division_code: string; issued_by?: string; passport_series?: string };
export type PassportData = {
  full_name: string;
  dob: string;
  pob: string;
  series: string;
  number: string;
  issued_by: string;
  issued_at: string;
  division_code: string;
  gender: "M" | "F";
};
export type KycMode = "sumsub" | "manual" | "idenfy";
export type ProcessConfig = {
  data_source: "internal" | "sumsub" | "idenfy";
  kyc_approval: "manual" | "provider" | "broker";
  doc_signing: "off" | "broker";
  requisites: "manual" | "broker";
  sherp_joinder?: "off" | "sherp";
};
export type KycStatus = {
  status: string;
  answer: "GREEN" | "RED" | null;
  provider_answer?: "GREEN" | "RED" | null;
  needs_issue_details?: boolean;
  needs_selfie?: boolean;
  kyc_cheat?: boolean;
  reject_labels?: string[] | null;
  moderation_comment?: string | null;
  applicant_id?: string | null;
  mode: KycMode;
  applicant_mode: KycMode | null;
  parsed_passport?: Record<string, unknown> | null;
  confirmed_profile?: PassportProfileIn | null;
  process_config?: ProcessConfig;
};

export type KycManualFile = { kind: string; mime: string; size: number; created_at?: string };
export type KycManualSubmitResult = {
  status: string;
  answer: "GREEN" | "RED" | null;
  parsed: Record<string, unknown> | null;
};

export const KYC_KINDS = ["passport_spread", "registration", "selfie_with_passport"] as const;
export type KycKind = (typeof KYC_KINDS)[number];

async function apiUpload<T>(path: string, fields: Record<string, string>, file: File): Promise<T> {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) fd.append(k, v);
  fd.append("file", file);
  const r = await fetch(API_URL + path, {
    method: "POST",
    headers: { ...initDataHeader() },
    body: fd,
    credentials: "include",
  });
  if (!r.ok) await throwApiError(r);
  return (await r.json()) as T;
}

export type KycUploadResult = {
  kind: string;
  mime: string;
  size: number;
  ok: boolean;
  reason: string | null;
  parsed: Record<string, unknown> | null;
};

export const kycApi = {
  accessToken: () => apiPost<KycAccessToken>("/v1/kyc/access-token"),
  idenfySession: () => apiPost<IdenfySession>("/v1/kyc/idenfy/session"),
  fmsUnit: (code: string) => apiGet<{ items: FmsUnit[] }>(`/v1/kyc/fms-unit?code=${encodeURIComponent(code)}`),
  issueDetails: (body: IssueDetailsIn) => apiPost<IssueDetailsIn>("/v1/kyc/issue-details", body),
  idenfySelfie: (file: File) =>
    apiUpload<{ kind: string; size: number; mime: string }>("/v1/kyc/idenfy/selfie", {}, file),
  status: () => apiGet<KycStatus>("/v1/kyc/status"),
  manualUpload: (kind: KycKind, file: File) =>
    apiUpload<KycUploadResult>("/v1/kyc/manual/file", { kind }, file),
  manualFiles: () => apiGet<{ files: KycManualFile[] }>("/v1/kyc/manual/files"),
  manualSubmit: () => apiPost<KycManualSubmitResult>("/v1/kyc/manual/submit"),
  saveProfile: (body: PassportProfileIn) =>
    apiPost<{ confirmed: boolean }>("/v1/kyc/profile", body),
};

export type PassportProfileIn = {
  first_name: string;
  last_name: string;
  middle_name?: string;
  date_of_birth: string; // YYYY-MM-DD
  passport_series?: string;
  passport_number: string;
  issued_at?: string; // YYYY-MM-DD
  division_code?: string; // NNN-NNN
  // страна резидентства; пусто → сервер запишет «Россия» (вариант анкеты по умолчанию)
  residency_country?: string;
};

export type RateInfo = { pair: string; rate: number; source: string; cached: boolean; stale?: boolean };
export const rateApi = {
  current: () => apiGet<RateInfo>("/v1/rate"),
};

// Банк-отправитель для экрана реквизитов. logo — data-URI картинки или Unicode-эмодзи.
// fee_pct — комиссия банка (справочно, в сумму заявки не входит); 0 — без комиссии.
export type PayerBank = { id: string; name: string; logo: string | null; fee_pct: number; comment: string | null };
export const banksApi = {
  list: () => apiGet<PayerBank[]>("/v1/banks"),
};

// Счёт клиента — рублёвый баланс. Появляется автоматически (без «выпуска»).
export type Account = { balance_rub: number; issued_at: string | null };
// USDT-актив: баланс кастодиального Tron-кошелька + его адрес и сеть.
export type UsdtAsset = { balance: number; address: string; network: string };
export type Assets = { rub: { balance: number }; usdt: UsdtAsset };
// видимость кнопок главного экрана — задаётся промокодом клиента (по умолчанию всё включено)
export type UiFlags = { show_support: boolean; show_referrals: boolean; show_cards: boolean };
// rate — курс USD(USDT)/RUB для конвертации общего баланса; null, если источник недоступен
export type AccountResponse = {
  account: Account;
  withdraw_enabled: boolean;
  assets: Assets;
  rate: number | null;
  ui?: UiFlags;
};
export const accountApi = {
  // withdraw_enabled — разблокирован ли вывод (чит-код hesoyam)
  get: () => apiGet<AccountResponse>("/v1/account"),
};

// Каталог карт «Выберите карту». image — фон панели (градиент+арт запечены), текст рисуем
// поверх. coming_soon → картинка приглушается + «Скоро» (карта пока недоступна к выпуску).
export type CatalogCard = {
  id: string;
  name: string;
  description: string | null;
  image: string | null;
  cost_rub: number | null;
  exchange_rate: number | null;
  currency: string;
  coming_soon: boolean;
  best_rate: boolean;
};
// Выпущенная карта клиента: одна на аккаунт, баланс в долларах. Номера/срока/CVV нет —
// показываем только last4 («•• 8075») и картинку дизайна из админки.
export type IssuedCard = {
  id: string;
  last4: string;
  balance_usd: number;
  currency: string;
  design: { id: string; name: string; image: string | null; description: string | null } | null;
  issued_at: string | null;
};
// Параметры обменов для форм. Курсы null — источник курса недоступен, обмен временно нельзя.
export type CardConfig = {
  buy_rate: number | null;
  sell_rate: number | null;
  fee_pct: number;
  crypto_fee_pct: number;
  crypto_fee_usdt: number;
  min_rub: number;
  max_rub: number;
};
export type CardState = { card: IssuedCard | null; account_rub: number; config: CardConfig };
// kind: issue | topup | refund | crypto_out | withdrawal; amount_usd — движение по карте со знаком
export type CardOperation = {
  id: string;
  kind: "issue" | "topup" | "refund" | "crypto_out" | "withdrawal";
  amount_usd: number;
  counter_amount: number | null;
  counter_asset: string | null;
  rate: number | null;
  fee_amount: number | null;
  fee_asset: string | null;
  status: "completed" | "pending" | "failed";
  error: string | null;
  created_at: string | null;
};
export type TopupQuote = { amount_rub: number; rate: number; fee_pct: number; fee_usd: number; net_usd: number };
export type RefundQuote = { amount_usd: number; rate: number; fee_pct: number; fee_rub: number; net_rub: number };
export type CryptoQuote = { amount_usd: number; fee_pct: number; fee_usdt: number; net_usdt: number };

export const cardsApi = {
  list: () => apiGet<{ cards: CatalogCard[]; issued: boolean }>("/v1/cards"),
  my: () => apiGet<CardState>("/v1/cards/my"),
  issue: (designId: string) => apiPost<{ card: IssuedCard }>("/v1/cards/issue", { design_id: designId }),
  quoteTopup: (amountRub: number) => apiPost<TopupQuote>("/v1/cards/quote/topup", { amount_rub: amountRub }),
  quoteRefund: (amountUsd: number) => apiPost<RefundQuote>("/v1/cards/quote/refund", { amount_usd: amountUsd }),
  quoteCrypto: (amountUsd: number) => apiPost<CryptoQuote>("/v1/cards/quote/crypto", { amount_usd: amountUsd }),
  topup: (amountRub: number) =>
    apiPost<{ card: IssuedCard; operation: CardOperation }>("/v1/cards/topup", { amount_rub: amountRub }),
  refund: (amountUsd: number) =>
    apiPost<{ card: IssuedCard; operation: CardOperation }>("/v1/cards/refund", { amount_usd: amountUsd }),
  crypto: (amountUsd: number, toAddress: string) =>
    apiPost<{ card: IssuedCard; operation: CardOperation }>("/v1/cards/crypto", {
      amount_usd: amountUsd,
      to_address: toAddress,
    }),
  operations: () => apiGet<{ operations: CardOperation[] }>("/v1/cards/operations"),
};

export type OrderRequisites = {
  type: string;
  beneficiary_bank: string;
  corr_account: string;
  bik: string;
  recipient_name: string;
  recipient_inn: string;
  recipient_kpp: string;
  settlement_account: string;
  purpose: string;
};

export type OrderStatusApi =
  | "awaiting_kyc"
  | "awaiting_payment"
  | "processing"
  | "paid"
  | "sending"
  | "completed"
  | "send_failed"
  | "rejected"
  | "cancelled";

export type ApiOrder = {
  id: string;
  reference: string;
  status: OrderStatusApi;
  amount_rub: number;
  amount_usdt: number;
  rate: number;
  wallet_address: string;
  network: string;
  created_at: string;
  requisites_issued_at: string | null;
  payment_claimed_at: string | null;
  paid_at: string | null;
  cancel_reason: string | null;
  tx_hash: string | null;
  hidden: boolean;
  requisites: OrderRequisites | null;
};

export const ordersApi = {
  create: (amount_rub: number, wallet_address?: string) =>
    apiPost<ApiOrder>("/v1/orders", { amount_rub, ...(wallet_address ? { wallet_address } : {}) }),
  list: () => apiGet<{ orders: ApiOrder[] }>("/v1/orders"),
  get: (id: string) => apiGet<ApiOrder>(`/v1/orders/${id}`),
  cancel: (id: string) => apiPost<ApiOrder>(`/v1/orders/${id}/cancel`),
  confirmPayment: (id: string) => apiPost<ApiOrder>(`/v1/orders/${id}/confirm-payment`),
  hide: (id: string) => apiPost<ApiOrder>(`/v1/orders/${id}/hide`),
  unhide: (id: string) => apiPost<ApiOrder>(`/v1/orders/${id}/unhide`),
  limits: () => apiGet<{ min_rub: number; max_rub: number }>("/v1/orders/limits"),
};

// ——— Вывод средств со счёта ———
export type WithdrawalMethod = "card" | "swift" | "crypto";
export type WithdrawalStatusApi = "pending" | "processing" | "completed" | "rejected" | "cancelled";

export type CardDest = { card_number: string; first_name: string; last_name: string };
export type SwiftDest = {
  beneficiary_name: string;
  iban: string;
  swift: string;
  bank_name: string;
  bank_country: string;
  purpose?: string; // назначение платежа (необязательно)
};
export type CryptoDest = { wallet_address: string };

export type ApiWithdrawal = {
  id: string;
  reference: string;
  method: WithdrawalMethod;
  source?: "account" | "card";  // откуда списаны деньги
  destination: Record<string, string>;
  amount_rub: number | null;   // списано со счёта (рубли)
  rate: number | null;         // зафиксированный курс ₽/USD
  amount_usdt: number;         // брутто в USD (amount_rub/rate)
  fee_usdt: number;
  net_usdt: number;            // к выплате клиенту
  status: WithdrawalStatusApi;
  cancel_reason: string | null;
  tx_hash: string | null;
  created_at: string;
  completed_at: string | null;
};

export type WithdrawConfig = { crypto_fee_usdt: number; crypto_network: string; withdraw_enabled: boolean };

export type CreateWithdrawalIn = {
  method: WithdrawalMethod;
  amount_rub: number;
  // source="card" — вывод с баланса выпущенной карты: сумма уже в долларах (amount_usd),
  // курс не применяется. Крипта с карты идёт отдельной операцией (cardsApi.crypto).
  source?: "account" | "card";
  amount_usd?: number;
  card?: CardDest;
  swift?: SwiftDest;
  crypto?: CryptoDest;
};

export const withdrawalsApi = {
  config: () => apiGet<WithdrawConfig>("/v1/withdrawals/config"),
  create: (body: CreateWithdrawalIn) => apiPost<ApiWithdrawal>("/v1/withdrawals", body),
  list: () => apiGet<{ withdrawals: ApiWithdrawal[] }>("/v1/withdrawals"),
  get: (id: string) => apiGet<ApiWithdrawal>(`/v1/withdrawals/${id}`),
  cancel: (id: string) => apiPost<ApiWithdrawal>(`/v1/withdrawals/${id}/cancel`),
  uploadInvoice: (id: string, file: File) =>
    apiUpload<{ kind: string; mime: string; size: number }>(`/v1/withdrawals/${id}/invoice`, {}, file),
};

// ——— Вывод USDT с кастодиального кошелька (он-чейн, без KYC) ———
export type UsdtWithdrawConfig = {
  network: string;
  address: string;      // адрес кастодиального кошелька (источник)
  balance_usdt: number;
  fee_usdt: number;
  max_usdt: number;     // максимум к отправке = balance − fee
};
export type WithdrawAddress = { address: string; label: string | null; last_used_at: string | null };
export type CustodialWithdrawalStatus = "pending" | "sending" | "completed" | "failed";
export type CustodialWithdrawal = {
  id: string;
  to_address: string;
  amount_usdt: number;
  fee_usdt: number;
  network: string;
  status: CustodialWithdrawalStatus;
  source: string;
  tx_hash: string | null;
  fee_tx_hash: string | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
};
export const usdtWithdrawApi = {
  config: () => apiGet<UsdtWithdrawConfig>("/v1/usdt-withdraw/config"),
  addresses: () => apiGet<{ addresses: WithdrawAddress[] }>("/v1/usdt-withdraw/addresses"),
  create: (to_address: string, amount_usdt: number, label?: string) =>
    apiPost<CustodialWithdrawal>("/v1/usdt-withdraw", { to_address, amount_usdt, ...(label ? { label } : {}) }),
  get: (id: string) => apiGet<CustodialWithdrawal>(`/v1/usdt-withdraw/${id}`),
  list: () => apiGet<{ withdrawals: CustodialWithdrawal[] }>("/v1/usdt-withdraw"),
};

// ——— ИПДС (источник происхождения денежных средств) — вторая ступень верификации ———
export type SofKind = "ndfl" | "income_certificate" | "other";
export type SofApplicationStatus = "pending" | "approved" | "rejected";
// Лимиты вывода клиента. Раздел лимитов/ИПДС показываем только при limit_reached (латч).
export type SofLimits = {
  limit_reached: boolean;
  monthly_limit_usd: number;
  monthly_used_usd: number;
  monthly_remaining_usd: number;
  month_reset_at: string;
  addl_total_usd: number;
  addl_used_usd: number;
  addl_remaining_usd: number;
  available_usd: number;
};
export type SofApplication = {
  id: string;
  status: SofApplicationStatus;
  comment: string | null;
  approved_amount_usd: number | null;
  reject_reason: string | null;
  files_count: number;
  submitted_at: string | null;
  reviewed_at: string | null;
};
export type SofOverview = {
  limits: SofLimits;
  kyc_ok: boolean;
  draft: { files_count: number; comment: string | null };
  applications: SofApplication[];
};
export type SofFileMeta = { id: string; kind: SofKind; mime: string; size: number; created_at: string | null };

export const sofApi = {
  overview: () => apiGet<SofOverview>("/v1/sof"),
  files: () => apiGet<{ files: SofFileMeta[] }>("/v1/sof/files"),
  uploadFile: (kind: SofKind, file: File) =>
    apiUpload<{ id: string; kind: string; mime: string; size: number }>("/v1/sof/file", { kind }, file),
  deleteFile: (id: string) => apiDelete<{ status: string }>(`/v1/sof/file/${id}`),
  submit: (comment?: string) => apiPost<SofOverview>("/v1/sof/submit", comment ? { comment } : {}),
};

export type WaitCallRequest = {
  call_id: string;
  call_phones: string[];
  status: string;
  expires_at: string;
};

export type WaitCallStatus = { status: string; user_id?: string | null };

export type AddressStruct = {
  country: string | null;
  city: string;
  address_line: string;
  postal_code: string | null;
  value?: string | null;
};

export type AddressSuggestion = {
  value: string;
  country: string | null;
  city: string | null;
  address_line: string;
  postal_code: string | null;
  has_house: boolean;
};

export const addressApi = {
  suggest: (q: string) =>
    apiGet<{ suggestions: AddressSuggestion[] }>(`/v1/address/suggest?q=${encodeURIComponent(q)}`),
  save: (a: { country?: string | null; city: string; address_line: string; postal_code?: string | null; value?: string | null }) =>
    apiPost<{ address: AddressStruct }>("/v1/address", a),
};

export type WebMe = {
  id: string;
  phone: string | null;
  email: string | null;
  address: AddressStruct | null;
  first_name: string | null;
  last_name: string | null;
  source: string;
};

export const authApi = {
  request: (phone: string) => apiPost<WaitCallRequest>("/v1/auth/phone/request", { phone }),
  check: (callId: string) => apiPost<WaitCallStatus>(`/v1/auth/phone/check/${callId}`),
  me: () => apiGet<WebMe>("/v1/auth/phone/me"),
  setEmail: (email: string) => apiPost<{ email: string }>("/v1/auth/phone/email", { email }),
  logout: () => apiPost<{ status: string }>("/v1/auth/phone/logout"),
  // Telegram-подтверждение номера: shared — номер из заверенного контакта (для модалки),
  // confirm — финальная привязка к аккаунту (users.phone) через _bind_or_merge.
  telegramShared: () => apiGet<{ phone: string | null }>("/v1/auth/phone/telegram-shared"),
  telegramConfirm: () =>
    apiPost<{ status: string; user_id: string; phone: string }>("/v1/auth/phone/telegram-confirm"),
};

// Вложение тянем как blob: в Telegram авторизация только через заголовок
// X-Telegram-Init-Data, а <img src> его не отправит. Возвращаем object URL.
export async function apiBlobUrl(path: string): Promise<string> {
  const r = await fetch(API_URL + path, {
    headers: { ...initDataHeader() },
    credentials: "include",
  });
  if (!r.ok) await throwApiError(r);
  const blob = await r.blob();
  return URL.createObjectURL(blob);
}

export type SupportAttachment = {
  name: string;
  mime: string;
  size: number;
  is_image: boolean;
  url: string;
};

export type SupportMessage = {
  id: string;
  sender: "user" | "admin";
  body: string | null;
  created_at: string;
  attachment: SupportAttachment | null;
};

export type SupportThread = {
  status: string;
  messages: SupportMessage[];
};

async function apiUploadFile<T>(path: string, file: File): Promise<T> {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(API_URL + path, {
    method: "POST",
    headers: { ...initDataHeader() },
    body: fd,
    credentials: "include",
  });
  if (!r.ok) await throwApiError(r);
  return (await r.json()) as T;
}

export const supportApi = {
  thread: () => apiGet<SupportThread>("/v1/support"),
  unread: () => apiGet<{ count: number }>("/v1/support/unread"),
  send: (text: string) => apiPost<SupportMessage>("/v1/support/messages", { text }),
  sendFile: (file: File) => apiUploadFile<SupportMessage>("/v1/support/messages/file", file),
  attachmentUrl: (url: string) => apiBlobUrl(url),
};

export type BrokerStatus = {
  active: boolean;
  onboarded: boolean;
  kyc_status: string | null;
  agreement_status: string | null;
  agreement_signed: boolean;
  client_status: string | null;
  signing_enabled?: boolean;
};

export type SigningDocument = {
  document_id: string;
  file_name: string | null;
  document_content_base64: string;
  document_content_type: string | null;
};

export type SignContext = {
  session_id: string;
  aggregate_hash: string | null;
  documents: { documentId: string; documentHashRendered: string }[];
};

export type SigningPackage = {
  already_signed: boolean;
  status?: string;
  expires_at?: string;
  documents?: SigningDocument[];
  sign_context?: SignContext;
};

export const brokerApi = {
  status: () => apiGet<BrokerStatus>("/v1/broker/status"),
  onboard: () =>
    apiPost<{ broker_client_id: number | null; kyc_approved: boolean; agreement_status: string | null }>(
      "/v1/broker/onboard",
    ),
  signingStart: () => apiPost<SigningPackage>("/v1/broker/signing/start"),
  sendOtp: (sign_context: SignContext) =>
    apiPost<{ otp_expires_at: string; otp_id: string }>("/v1/broker/signing/send-otp", { sign_context }),
  signingConfirm: (sign_context: SignContext, otp_id: string, otp: string) =>
    apiPost<{ status: string; signed: boolean }>("/v1/broker/signing/confirm", { sign_context, otp_id, otp }),
};

// ——— Договор присоединения ЗАО «ШЭРП» (платёжный оператор): своя email-OTP подпись ———
export type SherpStatus = {
  enabled: boolean;
  status: "none" | "draft" | "pending_otp" | "signed";
  signed: boolean;
  has_form: boolean;
  document_hash?: string | null;
  signed_at?: string | null;
  rules_version?: string | null;
};

export type SherpClient = {
  full_name: string;
  date_of_birth: string;
  passport: string;
  address: string;
  city: string;
  citizenship: string;
  phone: string;
  email: string;
};

export type SherpForm = {
  inn: string;
  snils: string;
  place_of_birth: string;
  citizenship: string;
  actual_address: string;
  is_pdl: boolean;
  pdl_details: string;
  fatca_us: boolean;
  fatca_tin: string;
  crs_details: string;
  source_of_funds: string;
  source_other: string;
  agree_all: boolean;
};

export type SherpPrefill = {
  client: SherpClient;
  form: SherpForm | null;
  source_of_funds_options: { value: string; label: string }[];
  consents: string[];
};

export type SherpDocument = {
  status: string;
  document_hash: string | null;
  html: string;
};

// ——— Реферальная программа: кабинет партнёра ———
// Код партнёра един с промокодом сервиса: он же ссылка, он же код для ручного ввода.

export type PartnerModel = "referral" | "agent";
export type PartnerStatus = "pending" | "approved" | "rejected" | "blocked";
export type PartnerPayoutMethod = "card" | "swift" | "crypto" | "internal";

export type PartnerProgram = {
  l1_pct: number;
  l2_pct: number;
  hold_days: number;
  min_payout_usd: number;
  payout_fees_usd: Record<PartnerPayoutMethod, number>;
  // максимум надбавки, которую агент может себе поставить; null — ограничения нет.
  // Базовую наценку TTEX (нашу маржу) партнёру не показываем — это внутренняя величина.
  max_markup_pct: number | null;
  offer_version: string;
};

export type PartnerBalance = {
  pending_usd: number;
  hold_usd: number;
  available_usd: number;
  paid_usd: number;
  total_earned_usd: number;
};

export type PartnerStats = {
  turnover_rub: number;
  deals: number;
  clients: number;
  active_clients: number;
};

export type PartnerVerification = {
  identity_verified: boolean;
  broker_onboarded: boolean;
  can_payout_crypto: boolean;
  can_payout_fiat: boolean;
  can_transfer_internal: boolean;
};

export type PartnerMe =
  | { is_partner: false; program: PartnerProgram }
  | {
      is_partner: true;
      status: PartnerStatus;
      model: PartnerModel;
      level: string;
      reject_reason: string | null;
      code: string | null;
      clicks: number;
      signups: number;
      markup_pct: number;
      base_rate: number | null;
      client_rate: number | null;
      balance: PartnerBalance;
      stats: PartnerStats;
      verification: PartnerVerification;
      program: PartnerProgram;
      created_at: string | null;
    };

export type PartnerReferral = {
  id: string;                 // обезличенный короткий идентификатор
  attached_at: string | null;
  kyc: "verified" | "pending" | "rejected" | "none";
  turnover_rub: number;
  earned_usd: number;
};

export type PartnerSubAgent = { id: string; turnover_rub: number; earned_usd: number };

export type PartnerAccrual = {
  id: string;
  level: "L1" | "L2";
  model: PartnerModel;
  turnover_rub: number;
  rate: number;
  rate_pct: number;
  amount_usd: number;
  status: "pending" | "hold" | "available" | "cancelled";
  needs_review: boolean;
  available_at: string | null;
  created_at: string | null;
};

export type PartnerPayout = {
  id: string;
  reference: string;
  method: PartnerPayoutMethod;
  destination: Record<string, string>;
  amount_usd: number;
  fee_usd: number;
  net_usd: number;
  rate: number | null;
  amount_rub: number | null;
  status: "pending" | "completed" | "rejected" | "cancelled";
  reject_reason: string | null;
  tx_hash: string | null;
  created_at: string | null;
  completed_at: string | null;
};

export type CreatePayoutIn = {
  method: PartnerPayoutMethod;
  amount_usd: number;
  card?: CardDest;
  swift?: SwiftDest;
  crypto?: CryptoDest;
};

export const partnerApi = {
  me: () => apiGet<PartnerMe>("/v1/partner/me"),
  offer: () => apiGet<{ version: string; text: string }>("/v1/partner/offer"),
  apply: (model: PartnerModel) =>
    apiPost<{ status: PartnerStatus; model: PartnerModel }>("/v1/partner/apply", {
      model,
      offer_accepted: true,
    }),
  setMarkup: (markup_pct: number) =>
    apiPut<{ markup_pct: number; client_rate: number }>("/v1/partner/markup", { markup_pct }),
  referrals: () =>
    apiGet<{ clients: PartnerReferral[]; sub_agents: PartnerSubAgent[] }>("/v1/partner/referrals"),
  accruals: () => apiGet<{ accruals: PartnerAccrual[] }>("/v1/partner/accruals"),
  payouts: () => apiGet<{ payouts: PartnerPayout[] }>("/v1/partner/payouts"),
  createPayout: (body: CreatePayoutIn) => apiPost<PartnerPayout>("/v1/partner/payouts", body),
  cancelPayout: (id: string) => apiPost<PartnerPayout>(`/v1/partner/payouts/${id}/cancel`),
  attach: (code: string) => apiPost<{ code: string; attached: boolean }>("/v1/partner/attach", { code }),
  checkCode: (code: string) =>
    apiGet<{ valid: boolean; code?: string; partner?: boolean }>(`/v1/partner/code/${encodeURIComponent(code)}`),
};

export const sherpApi = {
  status: () => apiGet<SherpStatus>("/v1/sherp/status"),
  prefill: () => apiGet<SherpPrefill>("/v1/sherp/prefill"),
  rules: () => apiGet<{ html: string }>("/v1/sherp/rules"),
  saveForm: (form: Partial<SherpForm>) => apiPost<SherpDocument>("/v1/sherp/form", { form }),
  document: () => apiGet<SherpDocument>("/v1/sherp/document"),
  sendOtp: () => apiPost<{ status: string; otp_sent_to: string | null }>("/v1/sherp/send-otp"),
  confirm: (otp: string) =>
    apiPost<{ status: string; signed: boolean; document_hash: string | null }>("/v1/sherp/confirm", { otp }),
};
